import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";
import { morph } from "@theme/morph";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /**
   * Cart drawer lines wrap the item row + welcome kit. Remove the whole group
   * so kits are not left behind and morph does not orphan content.
   * @param {HTMLElement} row
   * @returns {HTMLElement}
   */
  #getRemovableCartLineNode(row) {
    return row.closest(".cart-line") || row;
  }

  /**
   * @param {HTMLElement} row
   */
  #optimisticRemoveRow(row) {
    const node = this.#getRemovableCartLineNode(row);
    node.classList.add("cart-item--loading");

    const remove = () => node.remove();

    if (prefersReducedMotion()) {
      remove();
      return;
    }

    node.style.setProperty("--row-height", `${node.clientHeight}px`);
    node.classList.add("removing");
    onAnimationEnd(node, remove);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate,
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate,
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
  }

  /**
   * Multiplies each "Nx" amount in the per-unit template by quantity (e.g. "2x Refill, 1x Jar" with qty 3 → "6x Refill, 3x Jar").
   * @param {string} template - Per-unit text (e.g. "2x Refill pouch, 1x Jar").
   * @param {number} quantity - Line quantity.
   * @returns {string}
   */
  #scaleIncluded(template, quantity) {
    if (!template || quantity <= 0) return template || "";
    return template.replace(/(\d+)x/g, (_, n) => `${parseInt(n, 10) * quantity}x`);
  }

  /**
   * Reads current quantity from a cart row's quantity input.
   * @param {Element} row - Table row element.
   * @returns {number}
   */
  #getRowQuantity(row) {
    const input = row.querySelector("input[data-cart-line]");
    if (!input) return 0;
    const n = parseInt(input.value, 10);
    return Number.isNaN(n) ? 0 : n;
  }

  /**
   * Finds a cart row for a 1-based cart line number.
   * Prefers matching the quantity input so hidden/$0 rows stay aligned with cart order.
   * @param {number} line
   * @returns {HTMLElement|undefined}
   */
  #getCartRowByLine(line) {
    const cartItemRows = this.refs.cartItemRows;
    if (!cartItemRows?.length) return undefined;

    const matched = Array.from(cartItemRows).find((row) => {
      const input = row.querySelector(`input[data-cart-line="${line}"]`);
      return Boolean(input);
    });

    return matched || cartItemRows[line - 1];
  }

  /**
   * Finds a cart row by line-item key.
   * @param {string} key
   * @returns {HTMLElement|undefined}
   */
  #getCartRowByKey(key) {
    const cartItemRows = this.refs.cartItemRows;
    if (!key || !cartItemRows?.length) return undefined;
    return Array.from(cartItemRows).find((row) => row.dataset.key === key);
  }

  /**
   * Removes exactly one cart line by its unique key.
   * @param {string} key
   */
  #removeLineItemByKey(key) {
    const cartItemRowToRemove = this.#getCartRowByKey(key);

    if (!cartItemRowToRemove?.dataset.key) return;

    this.#optimisticRemoveRow(cartItemRowToRemove);
    this.updateQuantity({
      quantity: 0,
      action: "clear",
      key,
    });
  }

  /**
   * Remove button handler — always targets the clicked line key only.
   * @param {Event} event
   */
  onLineItemRemoveClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest("[data-line-key]");
    if (!(button instanceof HTMLElement)) return;

    const key = button.dataset.lineKey;
    if (!key) return;

    this.#removeLineItemByKey(key);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * When the row has connected variants (data-sync-variant-ids), syncs so that total quantity
   * of Y equals total quantity of X (including all lines with same variant: OTP + SUB, etc.).
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    const lineItemRow = this.#getCartRowByLine(line);
    if (!lineItemRow?.dataset.key) return;

    const syncVariantIdsStr = lineItemRow.dataset.syncVariantIds;
    const hasSync = syncVariantIdsStr && syncVariantIdsStr.trim() !== "";

    if (hasSync) {
      const sourceVariantId = parseInt(lineItemRow.dataset.variantId, 10);
      if (Number.isNaN(sourceVariantId)) return;

      // Total quantity of source variant (X) after this change = this line's new qty + all other lines with same variant
      let totalSourceQuantity = quantity;
      for (const row of Array.from(this.refs.cartItemRows || [])) {
        if (!row.dataset.key || row === lineItemRow) continue;
        const rowVariantId = parseInt(row.dataset.variantId, 10);
        if (rowVariantId === sourceVariantId) {
          totalSourceQuantity += this.#getRowQuantity(row);
        }
      }

      const syncVariantIds = syncVariantIdsStr
        .split(",")
        .map((id) => parseInt(id, 10))
        .filter((n) => !Number.isNaN(n));
      const updates = { [lineItemRow.dataset.key]: quantity };

      // Collect all Y rows (same order as DOM)
      const yRows = Array.from(this.refs.cartItemRows || []).filter((row) => {
        if (!row.dataset.key || row === lineItemRow) return false;
        const rowVariantId = parseInt(row.dataset.variantId, 10);
        return syncVariantIds.includes(rowVariantId);
      });

      // Distribute totalSourceQuantity across Y lines so total Y = totalSourceQuantity (e.g. 6 → 3+3 for 2 lines)
      const yCount = yRows.length;
      if (yCount > 0) {
        const perLine = Math.floor(totalSourceQuantity / yCount);
        let remainder = totalSourceQuantity % yCount;
        for (const row of yRows) {
          const qty = perLine + (remainder > 0 ? 1 : 0);
          if (remainder > 0) remainder -= 1;
          updates[row.dataset.key] = qty;
        }
      }

      if (Object.keys(updates).length > 1) {
        this.#updateMultipleQuantities(updates);
        return;
      }
    }

    if (quantity === 0) {
      return this.#removeLineItemByKey(lineItemRow.dataset.key);
    }

    const includesTemplate = lineItemRow.dataset.includesTemplate;
    let properties;

    if (includesTemplate && includesTemplate.trim() !== "") {
      let existing = {};
      try {
        const raw = JSON.parse(lineItemRow.dataset.lineProperties || "{}");
        existing = Array.isArray(raw) ? Object.fromEntries(raw) : raw;
      } catch { /* use empty */ }
      const { Includes: _, ...otherProperties } = existing;
      properties = { ...otherProperties, Includes: this.#scaleIncluded(includesTemplate.trim(), quantity) };
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
      key: lineItemRow.dataset.key,
      properties,
    });

    lineItemRow.classList.add("cart-item--loading");

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal by 1-based cart line number.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getCartRowByLine(line);
    if (!cartItemRowToRemove?.dataset.key) return;
    this.#removeLineItemByKey(cartItemRowToRemove.dataset.key);
  }

  // Click handler — kept for older markup; prefers the specific line key when present.
  onLineItemRemoveVariant(url) {
    const parts = String(url || "").split(",");
    const mainVariantId = parseInt(parts[0], 10);
    const cartItemRows = this.refs.cartItemRows;

    if (!cartItemRows || !cartItemRows.length || Number.isNaN(mainVariantId)) return;

    const mainRow = Array.from(cartItemRows).find((row) => {
      if (!row.dataset.key || row.hasAttribute("hidden")) return false;
      return parseInt(row.dataset.variantId, 10) === mainVariantId;
    });

    if (!mainRow?.dataset.key) return;
    this.#removeLineItemByKey(mainRow.dataset.key);
  }

  /**
   * Updates multiple line item quantities in one request (e.g. for connected variants).
   * Rows set to 0 are removed from the DOM with animation before the request.
   * @param {Record<string, number>} updates - Map of line item key to quantity.
   */
  #updateMultipleQuantities(updates) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      "multiple-update:user-action",
    );

    this.#disableCartItems();

    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    const cartItemRows = this.refs.cartItemRows;
    const keysToZero = Object.entries(updates)
      .filter(([, qty]) => qty === 0)
      .map(([key]) => key);

    // Optimistically remove rows that will be set to 0
    if (keysToZero.length > 0 && cartItemRows?.length) {
      for (const row of Array.from(cartItemRows)) {
        if (row.dataset.key && keysToZero.includes(row.dataset.key)) {
          this.#optimisticRemoveRow(row);
        }
      }
    }

    // Optimistically update all quantity inputs from updates (each row gets its new value)
    if (cartItemRows?.length) {
      for (const row of Array.from(cartItemRows)) {
        if (!row.dataset.key) continue;
        const qty = updates[row.dataset.key];
        if (qty === undefined) continue;
        row.classList.add("cart-item--loading");
        const input = row.querySelector("input[data-cart-line]");
        if (input) {
          input.value = String(qty);
          input.setAttribute("data-cart-quantity", String(qty));
          const selector = row.querySelector(
            "quantity-selector-component, cart-quantity-selector-component",
          );
          if (selector?.updateCartQuantity) selector.updateCartQuantity();
        }
      }
    }

    fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ updates }),
    })
      .then((response) => response.json())
      .then((cartData) => {
        resetShimmer(this);

        if (cartData.errors) {
          console.error("Cart update errors:", cartData.errors);
          this.#refreshCartItems();
          return;
        }

        const newCartItemCount = cartData.item_count ?? 0;
        this.#updateQuantitySelectors(cartData);
        this.dispatchEvent(
          new CartUpdateEvent(cartData, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
          }),
        );
        this.#refreshCartItems();

        this.#syncIncludedProperties(cartData);
      })
      .catch((error) => {
        console.error("Bulk quantity update failed:", error);
        this.#refreshCartItems();
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the Includes line item property to the scaled value (qty × per-unit) so checkout shows correct text.
   * @param {{ items?: Array<{ key: string, quantity: number, properties?: Record<string, string> }> }} cartData - Cart JSON.
   */
  #syncIncludedProperties(cartData) {
    const items = cartData?.items;
    if (!items || items.length === 0) return;

    const changeUrl = `${Theme.routes.cart_url}/change.js`;
    const cartItemRows = this.refs.cartItemRows;
    const promises = [];

    for (const item of items) {
      const current = item.properties?.Includes;
      if (!current || typeof current !== "string" || current.trim() === "")
        continue;

      const row = cartItemRows?.find((r) => r.dataset.key === item.key);
      const template = row?.dataset.includesTemplate?.trim();
      if (!template) continue;

      const scaled = this.#scaleIncluded(template, item.quantity);
      if (scaled === current.trim()) continue;

      const props = Array.isArray(item.properties) ? Object.fromEntries(item.properties) : item.properties;
      const { Includes: _, ...otherProperties } = props;

      promises.push(
        fetch(changeUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            id: item.key,
            quantity: item.quantity,
            properties: { ...otherProperties, Includes: scaled },
          }),
        }),
      );
    }

    if (promises.length === 0) return;
    Promise.all(promises).catch((err) =>
      console.warn("Sync Includes property:", err),
    );
  }

  /**
   * Deletes multiple items at once (sets quantity to 0).
   * @param {Record<string, number>} updates - Map of line item key to 0.
   */
  #deleteMultipleItems(updates) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      "multiple-clear:user-action",
    );

    this.#disableCartItems();

    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ updates }),
    })
      .then((response) => response.json())
      .then((cartData) => {
        resetShimmer(this);

        if (cartData.errors) {
          console.error("Cart update errors:", cartData.errors);
          return;
        }

        const newCartItemCount = cartData.item_count ?? 0;

        this.#updateQuantitySelectors(cartData);

        this.dispatchEvent(
          new CartUpdateEvent(cartData, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
          }),
        );

        this.#refreshCartItems();
      })
      .catch((error) => {
        console.error("Bulk delete failed:", error);
        this.#refreshCartItems();
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`,
    );

    this.#disableCartItems();

    const { line, quantity, key, properties } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const bodyPayload = {
      quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    };
    if (key) {
      bodyPayload.id = key;
    } else {
      bodyPayload.line = line;
    }
    if (properties && Object.keys(properties).length > 0)
      bodyPayload.properties = properties;

    const body = JSON.stringify(bodyPayload);

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html",
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]',
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsedResponseText.sections,
          }),
        );

        this.#morphCartItems(parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Morphs only this cart-items component from section HTML.
   * Avoids remorphing the header/cart-drawer shell while the drawer is open.
   * @param {string} [sectionHTML]
   */
  #morphCartItems(sectionHTML) {
    if (!sectionHTML) {
      this.#refreshCartItems();
      return;
    }

    const parsedDocument = new DOMParser().parseFromString(
      sectionHTML,
      "text/html",
    );
    const newCartItems =
      parsedDocument.querySelector(
        `cart-items-component[data-section-id="${this.sectionId}"]`,
      ) || parsedDocument.querySelector("cart-items-component");

    if (newCartItems && this.isConnected) {
      morph(this, newCartItems);
      this.#updateCartQuantitySelectorButtonStates();
      return;
    }

    morphSection(this.sectionId, sectionHTML);
  }

  /**
   * Fetches the section and morphs only the cart items content.
   */
  async #refreshCartItems() {
    try {
      const sectionHTML = await sectionRenderer.getSectionHTML(
        this.sectionId,
        false,
      );
      this.#morphCartItems(sectionHTML);
    } catch (error) {
      console.error(error);
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector("input");

    if (!quantityInput) throw new Error("Quantity input not found");

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement))
      throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      this.#refreshCartItems();
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#morphCartItems(cartItemsHtml);
    } else {
      this.#refreshCartItems();
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
    for (const row of this.querySelectorAll(".cart-item--loading")) {
      row.classList.remove("cart-item--loading");
    }
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"]`,
      );

      for (const selector of selectors) {
        const input = selector.querySelector("input[data-cart-quantity]");
        if (!input) continue;

        input.setAttribute("data-cart-quantity", item.quantity.toString());

        // Update the quantity selector's internal state
        if (
          "updateCartQuantity" in selector &&
          typeof selector.updateCartQuantity === "function"
        ) {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll(
      "cart-quantity-selector-component",
    )) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
